import { createWorkflowTemplateVersion } from '@commenda/api';
import {
  Button,
  FieldWrapper,
  Input,
  Label,
  SingleCombobox,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import {
  IWorkflowTemplate,
  TCreateWorkflowTemplateVersionParams,
} from '@commenda/types';
import { cn } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import mixpanel from '@commenda/mixpanel';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNotification } from '../../../hooks/useNotification';

const schema = z.object({
  version: z.string().min(1),
  versionNote: z.string().optional(),
  starterTemplateVersionId: z.string().optional(),
});

type TCreateWorkflowTemplateVersionFormFields = z.infer<typeof schema>;

type TCreateWorkflowTemplateVersionFormProps = {
  workflowTemplate: IWorkflowTemplate;
  onSuccess: () => void;
  onCancel: () => void;
  formContentClassName?: string;
  formFooterClassName?: string;
};

export const CreateWorkflowTemplateVersionForm = ({
  workflowTemplate,
  onSuccess,
  onCancel,
  formContentClassName,
  formFooterClassName,
}: TCreateWorkflowTemplateVersionFormProps) => {
  const isSubmitting = useToggle(false);
  const { setNotification } = useNotification();
  const { register, handleSubmit, watch, setValue } =
    useForm<TCreateWorkflowTemplateVersionFormFields>({
      resolver: zodResolver(schema),
    });

  const onSubmit = async (data: TCreateWorkflowTemplateVersionFormFields) => {
    isSubmitting.on();
    const response = await createWorkflowTemplateVersion({
      ...data,
      workflowTemplateId: workflowTemplate.id,
    } as TCreateWorkflowTemplateVersionParams);
    if (!response.success) {
      setNotification({
        title: 'Error',
        message: 'Failed to create workflow template version',
        show: true,
        valid: false,
      });
      isSubmitting.off();
      return;
    }
    setNotification({
      title: 'Success',
      message: 'Workflow template version created successfully',
      show: true,
      valid: true,
    });
    try {
      await mixpanel.track(MIXPANEL_EVENTS.WORKFLOW_VERSION_ADDED, {
      surface_area: MIXPANEL_SURFACE_AREA.WORKFLOW_AND_FORM_BUILDER,
        workflow_template_id: workflowTemplate.id,
        version: data.version,
      });
    } catch (error) {
      console.error('Error tracking workflow version added event:', error);
    }
    onSuccess();
    isSubmitting.off();
  };

  return (
    <div>
      <div className={cn('flex flex-col gap-4', formContentClassName)}>
        <FieldWrapper>
          <Label>Version</Label>
          <Input {...register('version')} />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Version Note</Label>
          <Textarea {...register('versionNote')} />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Starter Template Version</Label>
          <SingleCombobox
            {...register('starterTemplateVersionId')}
            options={workflowTemplate.versions.map((version) => ({
              label: version.version,
              value: version.id,
            }))}
            value={watch('starterTemplateVersionId')}
            setValue={(value) => setValue('starterTemplateVersionId', value)}
            placeholder="Select Starter Template Version"
          />
        </FieldWrapper>
      </div>
      <div className={cn('flex gap-2 justify-end', formFooterClassName)}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)}>Add Version</Button>
      </div>
    </div>
  );
};
